/**
 * Which report cards a user sees, from the desktop's report tree:
 * `frmDietech_Resident_DataGrid_V2.GetNodeVisibility` (frmDietech_Resident_DataGrid_V2.vb:6529-6620).
 *
 * The API applies the same rules to `GET api/Report/List` and the queue endpoints
 * (`Services/ReportVisibility.cs`). They are staged (docs/roadmap/web-permission-enforcement.md): the
 * catalog hides cards ONLY when the connection is enforced (`GET api/User/Me/PermissionEnforcement`
 * says Enforce); in Audit / Off it shows what it shows today. `reportVisibility.test.ts` reads the C#
 * table and fails when the two drift.
 *
 * Keys are desktop TREE nodes; a card reaches its node through `SLUG_TO_WINFORMS_TREE_KEY`
 * (reportFavoriteKeys.ts). A card with no node (website-only or per-resident reports) has no rule.
 *
 * Not copied (as on the server): the licence (Lite / Plus is unknown on the website, so the
 * Plus-only list is never applied) and purchasing (no card maps to tree 83-87).
 */
import type { SecurityRole } from '../../types/user';
import { SLUG_TO_WINFORMS_TREE_KEY } from './reportFavoriteKeys';

/** `lstLitePlusOnly`: hidden when the facility uses production. */
export const LITE_PLUS_ONLY: readonly number[] = [9, 11];
/** `lstPlusOnly`: hidden without production on a Lite licence (not applied: licence unknown). */
export const PLUS_ONLY: readonly number[] = [28, 62, 35, 26, 37, 16, 57, 20, 21, 27, 41, 33, 13, 15];
/** `lstProductionOnly`: hidden when the facility does not use production. */
export const PRODUCTION_ONLY: readonly number[] = [6, 14, 17, 34, 53, 54, 55, 56, 58, 59, 60, 72, 73, 74, 75, 37, 70, 71, 88, 89, 90, 91, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104];
/** `lstEHROnly`: shown exactly when the facility's EHR is active. */
export const EHR_ONLY: readonly number[] = [92, 93, 108];

/** The server's RoleLevel names, for the drift test, with the role column each reads. */
export const SECTION_ROLE_FIELD = {
  DietFluctuations: 'dietech_Diet_Fluctuations_Level',
  TubeFeed: 'dietech_Tube_Feed_Level',
  WeightHistory: 'dietech_Weight_History_Level',
  Recipe: 'dietech_Recipe_Level',
  TrayLine: 'dietech_Tray_Line_Level',
  Skin: 'dietech_Skin_Level',
  LikesDislikes: 'dietech_Likes_Dislikes_Level',
  NutritionalAssessment: 'dietech_Nutritional_Assessment_Level',
  SelectiveMenu: 'dietech_Selective_Menu_Level',
  Credit: 'dietech_Credit_Level',
  Employee: 'dietech_Employee_Level',
} as const satisfies Record<string, keyof SecurityRole>;

export type ReportSection = keyof typeof SECTION_ROLE_FIELD;

/** The per-section `Select Case` (.vb:6573-6601): the node needs this level above 0. */
export const SECTION_LEVEL: Readonly<Record<number, ReportSection>> = {
  23: 'DietFluctuations', 24: 'DietFluctuations', 25: 'DietFluctuations', 67: 'DietFluctuations',
  64: 'TubeFeed', 65: 'TubeFeed', 66: 'TubeFeed', 80: 'TubeFeed', 106: 'TubeFeed',
  48: 'WeightHistory', 49: 'WeightHistory', 50: 'WeightHistory', 51: 'WeightHistory', 52: 'WeightHistory',
  70: 'Recipe', 71: 'Recipe',
  77: 'TrayLine', 76: 'TrayLine', 20: 'TrayLine', 21: 'TrayLine', 81: 'TrayLine',
  47: 'Skin',
  11: 'LikesDislikes',
  42: 'NutritionalAssessment', 43: 'NutritionalAssessment', 45: 'NutritionalAssessment', 46: 'NutritionalAssessment',
  13: 'SelectiveMenu', 15: 'SelectiveMenu', 14: 'SelectiveMenu', 59: 'SelectiveMenu',
  72: 'Credit', 73: 'Credit', 74: 'Credit', 75: 'Credit', 88: 'Credit', 89: 'Credit',
  101: 'Employee', 103: 'Employee', 104: 'Employee', 107: 'Employee',
};

/** Case 42, 43, 45, 46 also need UseAssessment. */
export const NEEDS_USE_ASSESSMENT: readonly number[] = [42, 43, 45, 46];
/** Case 72-75, 88, 89 also need UseCredits. */
export const NEEDS_USE_CREDITS: readonly number[] = [72, 73, 74, 75, 88, 89];
/** Case 13, 15: the selective menu level is checked only with production. */
export const SECTION_ONLY_WITH_PRODUCTION: readonly number[] = [13, 15];
/** Case 91: the facility's BulkFirstChoice. */
export const NEEDS_BULK_FIRST_CHOICE: readonly number[] = [91];

/** The facility settings the tree reads; an unknown (undefined / null) setting never hides a card. */
export interface ReportVisibilityFacility {
  useProduction?: boolean | null;
  isCorporate?: boolean | null;
  useAssessment?: boolean | null;
  useCredits?: boolean | null;
  facilityEhrActive?: boolean | null;
  bulkFirstChoice?: boolean | null;
}

const isFalse = (v: boolean | null | undefined) => v === false;

/**
 * Why the desktop hides a tree node, or null when it shows. Same reasons as the server's
 * `ReportVisibility.HiddenReason`. Facility settings the page has not loaded never hide anything.
 */
export function hiddenReason(
  treeNode: number,
  facility: ReportVisibilityFacility | null | undefined,
  role: SecurityRole | null | undefined,
): string | null {
  const f = facility ?? {};
  // UseProduction OR IsCorporate (frmDietech_Resident_DataGrid_V2.vb:1205); unknown = no production rule.
  const production: boolean | null =
    f.useProduction == null && f.isCorporate == null ? null : !!f.useProduction || !!f.isCorporate;

  if (EHR_ONLY.includes(treeNode)) {
    if (isFalse(f.facilityEhrActive)) return 'FacilityEhrActive';
  } else if (production === true && LITE_PLUS_ONLY.includes(treeNode)) return 'NotUseProduction';
  else if (production === false && PRODUCTION_ONLY.includes(treeNode)) return 'UseProduction';

  if (NEEDS_BULK_FIRST_CHOICE.includes(treeNode)) return isFalse(f.bulkFirstChoice) ? 'BulkFirstChoice' : null;
  const section = SECTION_LEVEL[treeNode];
  if (!section) return null;
  if (SECTION_ONLY_WITH_PRODUCTION.includes(treeNode) && production !== true) return null;
  const level = role ? (role as unknown as Record<string, number | null | undefined>)[SECTION_ROLE_FIELD[section]] ?? 0 : 0;
  if (level <= 0) return section;
  if (NEEDS_USE_ASSESSMENT.includes(treeNode) && isFalse(f.useAssessment)) return 'UseAssessment';
  if (NEEDS_USE_CREDITS.includes(treeNode) && isFalse(f.useCredits)) return 'UseCredits';
  return null;
}

/**
 * Cards whose generation makes a staged write of its own, so the card also needs that write's
 * level (Enforce mode only). Not a tree rule, so the drift test against the C# table ignores it.
 *
 * Nutritional Assessment Template: its preSubmit creates the temporary header with
 * `POST api/Assessment/CreateBlankHeaderForReport`, staged at Nutritional Assessment 2
 * (AssessmentController; the wizard's FinishClick refuses at <= 1, Assessment_Wizard.vb:3069-3072).
 */
export const CARD_ACTION_LEVEL: Readonly<Record<string, { field: keyof SecurityRole; min: number }>> = {
  'assessment-template': { field: 'dietech_Nutritional_Assessment_Level', min: 2 },
};

/**
 * Website cards with no desktop tree node that print another card's report, so they follow that
 * card's tree rules (the server applies them when the report queues under the parent's pKey).
 * Not a favorites key: a favorite of these stays website-only.
 */
export const VISIBILITY_SAME_AS: Readonly<Record<string, string>> = {
};

/**
 * True when the catalog should hide this card: only when the connection is enforced, and the card
 * maps to a desktop tree node that is hidden for this role and facility, or misses its
 * CARD_ACTION_LEVEL.
 */
export function isReportHiddenByRole(
  slug: string,
  enforced: boolean,
  facility: ReportVisibilityFacility | null | undefined,
  role: SecurityRole | null | undefined,
): boolean {
  if (!enforced) return false;
  const action = CARD_ACTION_LEVEL[slug];
  if (action) {
    const level = role ? (role as unknown as Record<string, number | null | undefined>)[action.field] ?? 0 : 0;
    if (level < action.min) return true;
  }
  const node = SLUG_TO_WINFORMS_TREE_KEY[VISIBILITY_SAME_AS[slug] ?? slug];
  if (node == null) return false;
  return hiddenReason(node, facility, role) != null;
}
